import { ACTIVITY_SYSTEM_SCHEMA_NAMES } from '@app/data/activities/activities';
import {
  type ActivityAttributeType,
} from '@app/queries/activities/getActivityAttributes';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ALL_DATA_TYPES } from '@repo/advanced-filters';
import { InputTypeConfigType } from '@repo/utils/src/types/form/AssociableType';

export const CUSTOM_FIELD_PREFIX = 'mx_Custom_';

export interface ActivityAttributesState {
  editedAttributes: Record<string, Partial<ActivityAttributeType> & {
    deletedOptionIds?: number[];
    defaultOptionId?: string | number | null;
  }>;
  dataTypeConfigModal: {
    isOpen: boolean;
    selectedAttributeId: string | null;
  };
}

const initialState: ActivityAttributesState = {
  editedAttributes: {},
  dataTypeConfigModal: {
    isOpen: false,
    selectedAttributeId: null,
  },
};

export const activityAttributesSlice = createSlice({
  name: 'activities/attributes',
  initialState,
  reducers: {
    initializeAttributes: (
      state,
      action: PayloadAction<ActivityAttributeType[]>,
    ) => {
      state.editedAttributes = action.payload.reduce((acc, attribute) => {
        const defaultOption = attribute.options?.find((opt) => opt.isDefault);
        acc[attribute.id] = {
          attributeName: attribute.attributeName,
          schemaName: attribute.schemaName,
          isMandatory: attribute.isMandatory,
          dataType: attribute.dataType,
          dataTypeConfig: attribute.dataTypeConfig,
          options: [],
          deletedOptionIds: [],
          defaultOptionId: defaultOption ? Number(defaultOption.id) : null,
        };
        return acc;
      }, {} as Record<string, Partial<ActivityAttributeType> & {
        deletedOptionIds?: number[];
        defaultOptionId?: string | number | null;
      }>);
    },
    updateAttribute: (
      state,
      action: PayloadAction<{
        id: string;
        changes: Partial<ActivityAttributeType> & {
          deletedOptionIds?: number[];
          defaultOptionId?: string | number | null;
        };
      }>,
    ) => {
      const { id, changes } = action.payload;
      state.editedAttributes[id] = {
        ...state.editedAttributes[id],
        ...changes,
      };
    },
    addNewAttribute: (
      state,
    ) => {
      const id = crypto.randomUUID();

      const customFieldNumbers = Object.values(state.editedAttributes)
        .filter((attr) => attr.schemaName?.startsWith(CUSTOM_FIELD_PREFIX))
        .map((attr) => {
          const match = attr.schemaName?.match(new RegExp(`${CUSTOM_FIELD_PREFIX}(\\d+)`));
          return match ? parseInt(match[1] ?? '0', 10) : 0;
        });

      const nextNumber = Math.max(...customFieldNumbers, 0) + 1;

      const schemaName = `${CUSTOM_FIELD_PREFIX}${nextNumber}`;

      const fields = {
        attributeName: '',
        dataType: ALL_DATA_TYPES.string,
        schemaName,
        dataTypeConfig: {
          inputTypeConfig: InputTypeConfigType.TEXT_BOX,
          maxTextLength: 256,
        },
        isMandatory: false,
        activityAttributeType: 'custom',
        options: [],
        deletedOptionIds: [],
        defaultOptionId: null,
      };
      state.editedAttributes[id] = {
        ...fields,
      };
    },
    resetAttributes: (state) => {
      state.editedAttributes = {};
    },
    openDataTypeConfigModal: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.dataTypeConfigModal = {
        isOpen: true,
        selectedAttributeId: action.payload,
      };
    },
    closeDataTypeConfigModal: (state) => {
      state.dataTypeConfigModal = {
        isOpen: false,
        selectedAttributeId: null,
      };
    },
    deleteAttribute: (state, action: PayloadAction<string | number>) => {
      const id = action.payload;
      const attribute = state.editedAttributes[id];
      if (attribute && attribute.activityAttributeType !== 'system'
        && attribute.schemaName !== ACTIVITY_SYSTEM_SCHEMA_NAMES.status) {
        delete state.editedAttributes[id];
      }
    },
  },
});

export const {
  updateAttribute,
  resetAttributes,
  initializeAttributes,
  addNewAttribute,
  openDataTypeConfigModal,
  closeDataTypeConfigModal,
  deleteAttribute,
} = activityAttributesSlice.actions;
export default activityAttributesSlice.reducer;
